import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { ControlPoint, NutritionProductPlan, Plan, PlannedEffort, SectionData } from '../../services/PlansService';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import SectionTable from './SectionTable';
import { convertDistance, convertElevation } from '../../utils/UnitsUtils';
import { AppContext } from '../../contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import TimeBox from './TimeBox';
import NutritionBox from './NutritionBox';
import SectionChanger from '../common/SectionChanger';
import NoteBox from './NoteBox';
import AICoachBox from './AICoachBox';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import styled from '@mui/material/styles/styled';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Check from '@mui/icons-material/Check';
import Tooltip from '@mui/material/Tooltip';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import Tab from '@mui/material/Tab';
import FileDownload from '@mui/icons-material/FileDownload';
import Tabs from '@mui/material/Tabs';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import LocalPizzaOutlined from '@mui/icons-material/LocalPizzaOutlined';
import TimerOutlined from '@mui/icons-material/TimerOutlined';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import Badge from '@mui/material/Badge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { Permission } from '../../services/UserService';
import useRestricted from '../../hooks/useRestricted';
import PermissionDialog from '../common/PermissionDialog';
import Share from '@mui/icons-material/Share';
import DirectionsRun from '@mui/icons-material/DirectionsRun';
import DirectionsBike from '@mui/icons-material/DirectionsBike';
import { type SurfaceData } from '../../utils/getSurfaceData';
import ContentCopy from '@mui/icons-material/ContentCopy';
import posthog from 'posthog-js';
import Compare from '@mui/icons-material/Compare';
import ReviewBox from './ReviewBox';

dayjs.extend(localizedFormat);

const TempoPlannerContainer = styled(Box)(({ theme }) => ({
  overflow: "hidden",
  height: "calc(100vh - 164px)",
  [theme.breakpoints.down('md')]: {
    height: "auto",
  }
}));

const TopBox = styled(Box)(({ theme }) => ({
  //position: "sticky",
  //top: 0,
  borderBottom: `solid 1px ${theme.palette.divider}`
}));

const Col = styled(Box)(({ theme }) => ({
  width: 400,
  overflowY: "auto",
  "&:not(:last-child)": {
    borderRight: `solid 1px ${theme.palette.divider}`,
  },
  [theme.breakpoints.down('md')]: {
    width: "100%",
  }
}));

const PlannerTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  minHeight: 48,
  minWidth: 0,
  flex: 1,
  position: "relative",
}));


export interface TempoPlannerProps {
  plan: Plan;
  setPlanData: (newPlanData: Plan) => void;
  currentSection: number;
  changeSection: (section: number) => void;
  sectionsData: SectionData[];
  plot?: React.ReactNode;
  startLatlng: number[];
  endLatlng: number[];
  surfaceData?: SurfaceData[];
  onShare: () => void;
  onDuplicate: () => void;
  onCompare: () => void;
  isReviewer?: boolean;
  ownerName?: string;
}

type PlannerState = "pace" | "nutrition" | "notes" | "aicoach" | "review";
type ScenarioTab = "pessimistic" | "realistic" | "optimistic";

const TempoPlanner: React.FC<TempoPlannerProps> = observer(({ plan, setPlanData, currentSection, changeSection, sectionsData, plot, startLatlng, endLatlng, surfaceData, onShare, onDuplicate, onCompare, isReviewer, ownerName }) => {

  const intl = useIntl();
  const navigate = useNavigate();

  const permission = useRestricted();

  const theme = useTheme();
  const isUpMD = useMediaQuery(theme.breakpoints.up('md'));

  const { userService, plansService, reviewService } = React.useContext(AppContext);

  const distanceUnits = userService.user?.distanceUnits || "km";
  const elevationUnits = userService.user?.elevationUnits || "m";

  const [plannerState, setPlannerState] = React.useState<PlannerState>("pace");
  const [scenarioTab, setScenarioTab] = React.useState<ScenarioTab>("realistic");
  const [editingName, setEditingName] = React.useState<boolean>(false);
  const [currentName, setCurrentName] = React.useState<string>(plan.name || "");
  const [currentDate, setCurrentDate] = React.useState<dayjs.Dayjs | null>(plan.date ? dayjs(plan.date) : null);
  const [deleteOpen, setDeleteOpen] = React.useState<boolean>(false);
  const [nutritionPermissionOpen, setNutritionPermissionOpen] = React.useState<boolean>(false);
  const [aiCoachPermissionOpen, setAiCoachPermissionOpen] = React.useState<boolean>(false);
  const [exportPermissionOpen, setExportPermissionOpen] = React.useState<boolean>(false);
  const [comparePermissionOpen, setComparePermissionOpen] = React.useState<boolean>(false);
  const [scenarioPermissionOpen, setScenarioPermissionOpen] = React.useState<boolean>(false);
  const [reviewPermissionOpen, setReviewPermissionOpen] = React.useState<boolean>(false);

  const handleSaveName = () => {
    posthog.capture('plan_name_date_saved');
    setPlanData({ ...plan, name: currentName, date: currentDate ? currentDate.toDate() : plan.date });
    setEditingName(false);
  }

  const handleSaveControlPointData = (cpIndex: number, cpData: ControlPoint) => {
    posthog.capture('control_point_data_saved', { cpIndex });
    const newControlPoints = [...plan.controlPoints];
    newControlPoints[cpIndex] = cpData;
    setPlanData({ ...plan, controlPoints: newControlPoints });
    setEditingName(false);
  }

  const handleDelete = () => {
    posthog.capture('plan_deleted', { planId: plan.id });
    setDeleteOpen(false);
    if (!userService.user?.id || !plan.id) {
      return;
    }

    plansService.deletePlan(userService.user.id, plan.id).then(() => {
      navigate("/plans");
    });
  }

  const handleToggleSport = () => {
    posthog.capture('plan_sport_toggled', { sport: plan.sport });
    setPlanData({ ...plan, sport: plan.sport === "bike" ? "run" : "bike" });
  }

  const defaultScenarioPercentage = 10;

  const getEffortForScenario = (scenario: ScenarioTab): PlannedEffort[] => {
    if (scenario === "pessimistic") return plan.pessimisticEffort || [];
    if (scenario === "optimistic") return plan.optimisticEffort || [];
    return plan.plannedEffort;
  };

  const getScenarioEffortArray = (scenario: "pessimistic" | "optimistic"): PlannedEffort[] => {
    const targetLength = sectionsData.length;
    const existingScenario = scenario === "pessimistic" ? plan.pessimisticEffort : plan.optimisticEffort;

    const isDenseAndComplete = (arr: PlannedEffort[] | undefined): arr is PlannedEffort[] => {
      if (!arr || arr.length !== targetLength) return false;
      for (let i = 0; i < targetLength; i++) {
        if (arr[i] == null) return false;
      }
      return true;
    };

    if (isDenseAndComplete(existingScenario)) return [...existingScenario];

    const basePlannedEffort = plan.plannedEffort || [];
    const partialScenario: (PlannedEffort | undefined)[] = existingScenario || [];
    const result = new Array<PlannedEffort>(targetLength);
    for (let i = 0; i < targetLength; i++) {
      const existingEntry = partialScenario[i];
      const realisticTime = basePlannedEffort[i]?.time || 0;
      const computedTime = scenario === "pessimistic"
        ? Math.round(realisticTime * (1 + defaultScenarioPercentage / 100))
        : Math.max(0, Math.round(realisticTime * (1 - defaultScenarioPercentage / 100)));
      result[i] = {
        time: existingEntry?.time ?? computedTime,
        rpe: existingEntry?.rpe ?? 0,
        percentage: existingEntry?.percentage ?? defaultScenarioPercentage,
        locked: existingEntry?.locked ?? true,
      };
    }
    return result;
  };

  const setSectionTime = (time: number) => {
    posthog.capture('section_time_set', { section: currentSection, time, scenario: scenarioTab });

    if (scenarioTab === "pessimistic" || scenarioTab === "optimistic") {
      const realisticTime = plan.plannedEffort[currentSection]?.time || 0;
      const newEffortArray = getScenarioEffortArray(scenarioTab);
      const effectivePct = realisticTime > 0
        ? Math.max(0, Math.round(scenarioTab === "pessimistic"
            ? (time / realisticTime - 1) * 100
            : (1 - time / realisticTime) * 100))
        : (newEffortArray[currentSection]?.percentage ?? defaultScenarioPercentage);
      newEffortArray[currentSection] = { ...newEffortArray[currentSection], time, rpe: 0, percentage: effectivePct, locked: newEffortArray[currentSection]?.locked ?? true };
      setPlanData(scenarioTab === "pessimistic"
        ? { ...plan, pessimisticEffort: newEffortArray }
        : { ...plan, optimisticEffort: newEffortArray });
    } else {
      let newPlannedEffort = [...plan.plannedEffort];
      if (!plan.plannedEffort || plan.plannedEffort.length !== sectionsData.length) {
        newPlannedEffort = new Array(sectionsData.length).fill({ time: 0, rpe: 0 });
      }
      newPlannedEffort[currentSection] = { time, rpe: 0 };

      const updates: Partial<Plan> = { plannedEffort: newPlannedEffort };

      const pessSection = plan.pessimisticEffort?.[currentSection];
      if (pessSection && (pessSection.locked ?? true)) {
        const pct = pessSection.percentage ?? defaultScenarioPercentage;
        const newPess = getScenarioEffortArray("pessimistic");
        newPess[currentSection] = { ...newPess[currentSection], ...pessSection, time: Math.round(time * (1 + pct / 100)) };
        updates.pessimisticEffort = newPess;
      }

      const optSection = plan.optimisticEffort?.[currentSection];
      if (optSection && (optSection.locked ?? true)) {
        const pct = optSection.percentage ?? defaultScenarioPercentage;
        const newOpt = getScenarioEffortArray("optimistic");
        newOpt[currentSection] = { ...newOpt[currentSection], ...optSection, time: Math.max(0, Math.round(time * (1 - pct / 100))) };
        updates.optimisticEffort = newOpt;
      }

      setPlanData({ ...plan, ...updates });
    }
  };

  const handlePercentageChange = (pct: number) => {
    if (isNaN(pct) || pct < 0 || scenarioTab === "realistic") return;
    const scenario = scenarioTab as "pessimistic" | "optimistic";
    const realisticTime = plan.plannedEffort[currentSection]?.time || 0;
    const newTime = scenario === "pessimistic"
      ? Math.round(realisticTime * (1 + pct / 100))
      : Math.max(0, Math.round(realisticTime * (1 - pct / 100)));
    const newEffortArray = getScenarioEffortArray(scenario);
    newEffortArray[currentSection] = { ...newEffortArray[currentSection], time: newTime, percentage: pct };
    setPlanData(scenario === "pessimistic"
      ? { ...plan, pessimisticEffort: newEffortArray }
      : { ...plan, optimisticEffort: newEffortArray });
    setHours(Math.floor(newTime / 60));
    setMinutes(newTime % 60);
  };

  const handleToggleLock = (locked: boolean) => {
    if (scenarioTab === "realistic") return;
    const scenario = scenarioTab as "pessimistic" | "optimistic";
    const newEffortArray = getScenarioEffortArray(scenario);
    const pct = newEffortArray[currentSection]?.percentage ?? defaultScenarioPercentage;
    const realisticTime = plan.plannedEffort[currentSection]?.time || 0;
    const newTime = locked
      ? (scenario === "pessimistic"
          ? Math.round(realisticTime * (1 + pct / 100))
          : Math.max(0, Math.round(realisticTime * (1 - pct / 100))))
      : newEffortArray[currentSection]?.time || 0;
    newEffortArray[currentSection] = { ...newEffortArray[currentSection], time: newTime, locked, percentage: pct };
    setPlanData(scenario === "pessimistic"
      ? { ...plan, pessimisticEffort: newEffortArray }
      : { ...plan, optimisticEffort: newEffortArray });
    if (locked) {
      setHours(Math.floor(newTime / 60));
      setMinutes(newTime % 60);
    }
  };

  const setSectionNutrition = (sectionNutrition: NutritionProductPlan[]) => {
    const nutritionCount = sectionNutrition.length;
    posthog.capture('section_nutrition_set', { section: currentSection, nutritionCount });
    let newNutrition = [...(plan.plannedNutrition ? plan.plannedNutrition : [])];
    if (!plan.plannedNutrition || plan.plannedNutrition.length !== sectionsData.length) {
      newNutrition = new Array(sectionsData.length).fill([]);
    }
    newNutrition[currentSection] = { products: sectionNutrition };
    setPlanData({ ...plan, plannedNutrition: newNutrition });
  }

  const setSectionNote = (note: string) => {
    posthog.capture('section_note_set', { section: currentSection, noteLength: note.length });
    let newNotes = [...(plan.notes ? plan.notes : [])];
    if (!plan.notes || plan.notes.length !== sectionsData.length) {
      newNotes = new Array(sectionsData.length).fill("");
    }
    newNotes[currentSection] = note;
    setPlanData({ ...plan, notes: newNotes });
  }

  const activeEffort = getEffortForScenario(scenarioTab);
  const totalTime = activeEffort.reduce((acc, effort) => acc + (effort?.time ?? 0), 0);
  const totalTimeBefore = activeEffort.slice(0, currentSection).reduce((acc, effort) => acc + (effort?.time ?? 0), 0);

  const sectionTime = activeEffort[currentSection]?.time || 0;
  const [hours, setHours] = React.useState<number>(sectionTime ? Math.floor(sectionTime / 60) : 0);
  const [minutes, setMinutes] = React.useState<number>(sectionTime ? sectionTime % 60 : 0);

  React.useEffect(() => {
    const scenarioEffort = scenarioTab === "pessimistic" ? (plan.pessimisticEffort || []) :
      scenarioTab === "optimistic" ? (plan.optimisticEffort || []) :
      plan.plannedEffort;

    // Auto-initialize scenario with default % when all times are 0 but realistic has data
    if (scenarioTab !== "realistic") {
      const allZero = scenarioEffort.every(e => !e?.time);
      const realisticHasTime = plan.plannedEffort.some(e => (e?.time || 0) > 0);
      if (allZero && realisticHasTime) {
        const newEffort = plan.plannedEffort.map(e => {
          const rt = e?.time || 0;
          return {
            time: scenarioTab === "pessimistic"
              ? Math.round(rt * (1 + defaultScenarioPercentage / 100))
              : Math.max(0, Math.round(rt * (1 - defaultScenarioPercentage / 100))),
            rpe: 0,
            percentage: defaultScenarioPercentage,
            locked: true,
          };
        });
        setPlanData(scenarioTab === "pessimistic"
          ? { ...plan, pessimisticEffort: newEffort }
          : { ...plan, optimisticEffort: newEffort });
        return;
      }
    }

    const st = scenarioEffort[currentSection]?.time || 0;
    setHours(st ? Math.floor(st / 60) : 0);
    setMinutes(st ? st % 60 : 0);
  }, [currentSection, scenarioTab, plan, plan.plannedEffort, plan.pessimisticEffort, plan.optimisticEffort, setPlanData]);

  const currentSectionScenarioEffort = scenarioTab !== "realistic" ? activeEffort[currentSection] : null;
  const displayPercentage = currentSectionScenarioEffort?.percentage ?? defaultScenarioPercentage;
  const isLocked = currentSectionScenarioEffort?.locked ?? true;


  const onChangeSectionTime = (time: number) => {
    setSectionTime(time);
    setHours(Math.floor(time / 60));
    setMinutes(time % 60);
  }

  const onChangeCutoff = (cutOff: Date | null) => {
    const controlPoint = {
      ...sectionsData[currentSection],
      cutOff: cutOff
    } as ControlPoint;

    handleSaveControlPointData(Math.floor(currentSection / 2), controlPoint);
  }

  return (

    <Box>

      <TopBox>
        {/* On mobile: actions row above the title */}
        {!isReviewer && !isUpMD && (
          <Box display="flex" alignItems="center" paddingX={2} paddingTop={1} gap={0.5}>
            <Tooltip title={plan.sport === "bike" ? intl.formatMessage({ id: "action.changeToRun" }) : intl.formatMessage({ id: "action.changeToBike" })}>
              <IconButton onClick={handleToggleSport} size="small">
                {plan.sport === "bike" ? <DirectionsBike fontSize="small" /> : <DirectionsRun fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "action.compare" })}>
              <IconButton onClick={permission === Permission.free ? () => setComparePermissionOpen(true) : onCompare} size="small">
                <Compare fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "action.duplicate" })}>
              <IconButton onClick={onDuplicate} size="small">
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "action.shareTemplate" })}>
              <IconButton onClick={onShare} size="small">
                <Share fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "plans.export" })}>
              <IconButton
                onClick={() => {
                  posthog.capture('plan_export_clicked', { planId: plan.id });
                  if (permission === Permission.free) { setExportPermissionOpen(true); return; }
                  navigate(`/export/${userService.user?.id}/${plan.id}`)
                }}
                size="small"
              >
                <FileDownload fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "action.delete" })}>
              <IconButton onClick={() => setDeleteOpen(true)} size="small">
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Box display="flex" alignItems="center" paddingX={2} paddingTop={isUpMD ? 2 : 0.5}>
          {!isReviewer && editingName ?
            <Box marginRight={1}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="planName"
                name="planName"
                variant="standard"
                size="small"
                value={currentName}
                autoFocus
                onChange={(event) => setCurrentName(event.target.value)}
              />
            </Box> :
            (<Typography variant="h6" marginRight={1}>{plan.name}</Typography>)}
          {!isReviewer && (editingName ?
            (<IconButton onClick={handleSaveName} disabled={!currentName} size="small">
              <Check fontSize="small" />
            </IconButton>) :
            (<IconButton onClick={() => {
              posthog.capture('plan_edit_name_date_clicked');
              setEditingName(true)
            }} size="small">
              <Tooltip title={intl.formatMessage({ id: "action.editNameDate" })}>
                <Edit fontSize="small" />
              </Tooltip>
            </IconButton>))}

          {!isReviewer && isUpMD && <Box marginLeft="auto" display="flex" alignItems="center" gap={1}>
            <Tooltip title={plan.sport === "bike" ? intl.formatMessage({ id: "action.changeToRun" }) : intl.formatMessage({ id: "action.changeToBike" })}>
              <IconButton onClick={handleToggleSport} size="small">
                {plan.sport === "bike" ? <DirectionsBike fontSize="small" /> : <DirectionsRun fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "action.compare" })}>
              <IconButton
                onClick={permission === Permission.free ? () => setComparePermissionOpen(true) : onCompare}
                size="small">
                <Compare fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "action.duplicate" })}>
              <IconButton onClick={onDuplicate} size="small">
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "action.shareTemplate" })}>
              <IconButton onClick={onShare} size="small">
                <Share fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "plans.export" })}>
              <IconButton
                onClick={() => {
                  posthog.capture('plan_export_clicked', { planId: plan.id });
                  if (permission === Permission.free) {
                    setExportPermissionOpen(true);
                    return;
                  }
                  navigate(`/export/${userService.user?.id}/${plan.id}`)
                }}
                size="small"
              >
                <FileDownload fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: "action.delete" })}>
              <IconButton onClick={() => setDeleteOpen(true)} size="small">
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>}
        </Box>


        <Box marginTop={1} marginBottom={1} paddingX={2} display={isUpMD ? "flex" : "grid"} gridTemplateColumns="auto auto" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" fontSize={12} fontWeight="700">{intl.formatMessage({ id: "data.distanceTitle" })}</Typography>
            <Typography variant="body2">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertDistance(plan.distance, distanceUnits), unit: intl.formatMessage({ id: distanceUnits }) })}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" fontSize={12} fontWeight="700">{intl.formatMessage({ id: "data.elevationGainTitle" })}</Typography>
            <Typography variant="body2">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(plan.elevationGain, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" fontSize={12} fontWeight="700">{intl.formatMessage({ id: "data.elevationLossTitle" })}</Typography>
            <Typography variant="body2">{intl.formatMessage({ id: "data.valueUnit" }, { value: convertElevation(plan.elevationLoss, elevationUnits), unit: intl.formatMessage({ id: elevationUnits }) })}</Typography>
          </Box>
          <Grid item xs={6} marginY={1}>
            <Typography variant="body2" fontSize={12} fontWeight="700">{intl.formatMessage({ id: "data.dateTitle" })}</Typography>
            {
              !isReviewer && editingName ?
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    value={currentDate}
                    onChange={(newValue) => {
                      setCurrentDate(newValue);
                    }
                    }
                    slotProps={
                      {
                        textField: {
                          variant: 'standard',
                          size: 'small',
                          sx: {
                            "& input": {
                              fontSize: "0.875rem"
                            }
                          }
                        },
                      }
                    }
                  />
                </LocalizationProvider> :
                <Typography variant="body2">{dayjs(plan.date).locale(intl.locale).format("lll")}</Typography>
            }
          </Grid>
        </Box>
      </TopBox>

      <SectionChanger
        currentSection={currentSection}
        changeSection={changeSection}
        sectionsData={sectionsData}
      />

      <TempoPlannerContainer display={isUpMD ? "flex" : "block"}>

        <Col display="flex" flexDirection="column" justifyContent="space-between">

          <Box>
            {!!sectionsData[currentSection] && <>
              {sectionsData[currentSection].type === "section" && plot}

              {sectionsData.length > currentSection && <SectionTable
                planId={plan.id}
                currentSection={currentSection}
                sectionData={sectionsData[currentSection]}
                onSaveControlPoint={handleSaveControlPointData}
                surfaceData={surfaceData}
                readOnly={isReviewer}
                startLatlng={startLatlng}
                endLatlng={endLatlng}
              />}
            </>}
          </Box>

        </Col>
        <Col sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flexShrink: 0 }}>
          <Tabs
            value={plannerState}
            onChange={(ev, value) => {
              posthog.capture('planner_tab_changed', { tab: value });
              if (value === "nutrition" && permission === Permission.free && !isReviewer) {
                setNutritionPermissionOpen(true);
                return;
              }
              if (value === "aicoach" && permission === Permission.free) {
                setAiCoachPermissionOpen(true);
                return;
              }
              if (value === "review" && permission === Permission.free && !isReviewer) {
                setReviewPermissionOpen(true);
                return;
              }
              setPlannerState(value)
            }}
            variant="fullWidth"
            aria-label="tabs"
          >
            {plannerState === "pace" ? <PlannerTab icon={<TimerOutlined />} iconPosition="start" value="pace" />
              : <Tooltip title={intl.formatMessage({ id: "plans.pace" })} placement="top">
                <PlannerTab icon={<TimerOutlined />} iconPosition="start" value="pace" />
              </Tooltip>
            }
            {plannerState === "nutrition" ? <PlannerTab icon={<LocalPizzaOutlined />} iconPosition="start" value="nutrition" />
              : <Tooltip title={intl.formatMessage({ id: "plans.nutrition" })} placement="top">
                <PlannerTab icon={<LocalPizzaOutlined />} iconPosition="start" value="nutrition" />
              </Tooltip>
            }
            {plannerState === "notes" ? <PlannerTab icon={<DescriptionOutlined />} iconPosition="start" value="notes" />
              : <Tooltip title={intl.formatMessage({ id: "plans.notes" })} placement="top">
                <PlannerTab icon={<DescriptionOutlined />} iconPosition="start" value="notes" />
              </Tooltip>
            }
            {!isReviewer && (plannerState === "aicoach" ? <PlannerTab icon={<AutoAwesome />} iconPosition="start" value="aicoach" />
              : <Tooltip title={intl.formatMessage({ id: "plans.aiCoach" })} placement="top">
                <PlannerTab icon={<AutoAwesome />} iconPosition="start" value="aicoach" />
              </Tooltip>
            )}
            {plannerState === "review" ? <PlannerTab icon={<Badge badgeContent={reviewService.getUnreadForPlan(plan.id, userService.user?.id || "")} color="error"><ForumOutlined /></Badge>} iconPosition="start" value="review" />
              : <Tooltip title={intl.formatMessage({ id: "review.tab" })} placement="top">
                <PlannerTab icon={<Badge badgeContent={reviewService.getUnreadForPlan(plan.id, userService.user?.id || "")} color="error"><ForumOutlined /></Badge>} iconPosition="start" value="review" />
              </Tooltip>
            }
          </Tabs>
          {plannerState === "pace" && !isReviewer && (
            <Tabs
              value={scenarioTab}
              onChange={(_, value) => {
                if ((value === "pessimistic" || value === "optimistic") && permission === Permission.free) {
                  setScenarioPermissionOpen(true);
                  return;
                }
                setScenarioTab(value as ScenarioTab);
              }}
              variant="fullWidth"
              aria-label="scenario tabs"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label={intl.formatMessage({ id: "plans.pessimistic" })} value="pessimistic" sx={{ textTransform: "none", minHeight: 36 }} />
              <Tab label={intl.formatMessage({ id: "plans.realistic" })} value="realistic" sx={{ textTransform: "none", minHeight: 36 }} />
              <Tab label={intl.formatMessage({ id: "plans.optimistic" })} value="optimistic" sx={{ textTransform: "none", minHeight: 36 }} />
            </Tabs>
          )}
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {isReviewer && (
            <Box px={2} py={1} bgcolor="action.hover" borderBottom={1} borderColor="divider" sx={{ flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {ownerName
                  ? intl.formatMessage({ id: 'review.readOnlyBy' }, { name: ownerName })
                  : intl.formatMessage({ id: 'review.readOnly' })}
              </Typography>
            </Box>
          )}
          {!!sectionsData[currentSection] && plannerState === "pace" && (
            <TimeBox
              section={sectionsData[currentSection]}
              hours={hours}
              minutes={minutes}
              onChangeSectionTime={onChangeSectionTime}
              startDate={plan.date}
              totalTime={totalTime}
              totalTimeBefore={totalTimeBefore}
              totalDistance={plan.distance}
              onChangeCutoff={onChangeCutoff}
              sport={plan.sport || "run"}
              scenarioPercentage={scenarioTab !== "realistic" ? displayPercentage : undefined}
              scenarioLocked={scenarioTab !== "realistic" ? isLocked : undefined}
              scenarioLabel={scenarioTab !== "realistic" ? intl.formatMessage({ id: scenarioTab === "pessimistic" ? "plans.slowerThanRealistic" : "plans.fasterThanRealistic" }) : undefined}
              onScenarioPercentageChange={scenarioTab !== "realistic" ? handlePercentageChange : undefined}
              onScenarioLockToggle={scenarioTab !== "realistic" ? handleToggleLock : undefined}
              readOnly={isReviewer}
            />
          )}
          {!!sectionsData[currentSection] && plannerState === "nutrition" && <NutritionBox totalTime={totalTime} totalNutrition={plan.plannedNutrition} sectionNutrition={plan.plannedNutrition && plan.plannedNutrition.length ? plan.plannedNutrition[currentSection]?.products || [] : []} setSectionNutrition={setSectionNutrition} readOnly={isReviewer} ownerId={isReviewer ? plan.userId : undefined} />}
          {!!sectionsData[currentSection] && plannerState === "notes" && (
            <NoteBox currentSection={currentSection} note={plan.notes && plan.notes.length > currentSection ? plan.notes[currentSection] : ""} onChangeNote={setSectionNote} readOnly={isReviewer} authorName={ownerName} />
          )}
          {plannerState === "aicoach" && (
            <AICoachBox
              plan={plan}
              sectionsData={sectionsData}
              currentSection={currentSection}
              sectionType={sectionsData[currentSection]?.type}
            />
          )}
          {plannerState === "review" && userService.user?.id && (
            <ReviewBox
              planId={plan.id}
              planOwnerId={plan.userId}
              currentSection={currentSection}
              currentUserId={userService.user.id}
              isOwner={!isReviewer}
            />
          )}
          </Box>
        </Col>

        <Dialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="xs"
        >
          <DialogTitle id="alert-dialog-title">
            {intl.formatMessage({ id: "plans.deletePlan" })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {intl.formatMessage({ id: "plans.deletePlanConfirm" })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>
              {intl.formatMessage({ id: "action.cancel" })}
            </Button>
            <Button onClick={handleDelete} autoFocus>
              {intl.formatMessage({ id: "action.delete" })}
            </Button>
          </DialogActions>
        </Dialog>

      </TempoPlannerContainer>
      <PermissionDialog
        open={nutritionPermissionOpen}
        onClose={() => setNutritionPermissionOpen(false)}
        title={intl.formatMessage({ id: "permission.nutritionTitle" })}
        text={intl.formatMessage({ id: "permission.nutritionText" })}
        video="/videos/nutrition.mp4"
      />
      <PermissionDialog
        open={aiCoachPermissionOpen}
        onClose={() => setAiCoachPermissionOpen(false)}
        title={intl.formatMessage({ id: "permission.aiCoachTitle" })}
        text={intl.formatMessage({ id: "permission.aiCoachText" })}
        video="/videos/aicoach.mp4"
      />
      <PermissionDialog
        open={exportPermissionOpen}
        onClose={() => setExportPermissionOpen(false)}
        title={intl.formatMessage({ id: "permission.exportTitle" })}
        text={intl.formatMessage({ id: "permission.exportText" })}
        video="/videos/export.mp4"
      />
      <PermissionDialog
        open={comparePermissionOpen}
        onClose={() => setComparePermissionOpen(false)}
        title={intl.formatMessage({ id: "permission.compareTitle" })}
        text={intl.formatMessage({ id: "permission.compareText" })}
        video="/videos/compare.mp4"
      />
      <PermissionDialog
        open={scenarioPermissionOpen}
        onClose={() => setScenarioPermissionOpen(false)}
        title={intl.formatMessage({ id: "permission.scenariosTitle" })}
        text={intl.formatMessage({ id: "permission.scenariosText" })}
        video="/videos/scenarios.mp4"
      />
      <PermissionDialog
        open={reviewPermissionOpen}
        onClose={() => setReviewPermissionOpen(false)}
        title={intl.formatMessage({ id: "permission.reviewTitle" })}
        text={intl.formatMessage({ id: "permission.reviewText" })}
        video="/videos/review.mp4"
      />
    </Box>
  );
});

export default TempoPlanner;